import { useEffect } from 'react'
import { useMatches } from '@tanstack/react-router'

/**
 * 라우트 경로 기반 테마 자동 스위칭 훅.
 *
 * Public 라우트(/apply/*, /$shortCode/*)에서는 data-theme="public" 적용,
 * 나머지(Dashboard, Admin)에서는 제거하여 기본 Dashboard 테마 사용.
 *
 * TanStack Router의 layout route 매칭을 활용하여,
 * 새 경로 추가 시 수동 업데이트가 불필요하다.
 */
export function useTheme() {
  const matches = useMatches()

  useEffect(() => {
    const isPublic = isPublicRoute(matches)
    const html = document.documentElement

    if (isPublic) {
      html.setAttribute('data-theme', 'public')
    } else {
      html.removeAttribute('data-theme')
    }

    return () => {
      html.removeAttribute('data-theme')
    }
  }, [matches])
}

/**
 * Public 라우트 판별.
 *
 * /_dashboard, /admin 레이아웃에 매칭되면 Dashboard/Admin.
 * /apply/ 경로이거나, 위 두 레이아웃에 매칭되지 않는 1depth 경로는 Public.
 */
function isPublicRoute(matches: { id: string; pathname: string }[]): boolean {
  const pathname = matches[matches.length - 1]?.pathname ?? '/'

  // /apply/로 시작하면 항상 Public
  if (pathname.startsWith('/apply/')) return true

  // root(/) 정확 매치는 Dashboard
  if (pathname === '/') return false

  // _dashboard 또는 admin 레이아웃에 매칭되면 Dashboard/Admin
  const hasInternalLayout = matches.some(
    (m) => m.id === '/_dashboard' || m.id.startsWith('/admin'),
  )
  if (hasInternalLayout) return false

  // auth, shop 관련 유틸 경로는 Dashboard 테마
  if (pathname.startsWith('/auth.') || pathname.startsWith('/shop-') || pathname.startsWith('/notice')) {
    return false
  }

  // 나머지 = 단축 URL → Public
  return true
}
